import type { ARRHENIUS_EQUATION_CALCULATOR_STRINGS } from "@/components/tools/arrhenius-equation-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import type { ArrheniusFit } from "@/lib/tool-kit/chem-physical/arrhenius";

type Strings = typeof ARRHENIUS_EQUATION_CALCULATOR_STRINGS;

const WIDTH = 480;
const HEIGHT = 280;
const LEFT = 56;
const RIGHT = 18;
const TOP = 16;
const BOTTOM = 48;
const PAD = 0.08;
/** x is plotted as 1000/T so the axis reads in convenient numbers. */
const X_SCALE = 1000;

/** ln k against 1000/T with the fitted line. */
export function ArrheniusPlot({
  fit,
  strings,
}: {
  fit: ArrheniusFit;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const xs = fit.points.map((p) => p.x * X_SCALE);
  const ys = fit.points.map((p) => p.y);
  const xSpan = Math.max(...xs) - Math.min(...xs) || 1;
  const xLo = Math.min(...xs) - xSpan * PAD;
  const xHi = Math.max(...xs) + xSpan * PAD;
  const lineAt = (x: number) => fit.slope * (x / X_SCALE) + fit.intercept;
  const yAll = [...ys, lineAt(xLo), lineAt(xHi)];
  const ySpan = Math.max(...yAll) - Math.min(...yAll) || 1;
  const yLo = Math.min(...yAll) - ySpan * PAD;
  const yHi = Math.max(...yAll) + ySpan * PAD;
  const px = (x: number) =>
    LEFT + ((x - xLo) / (xHi - xLo)) * (WIDTH - LEFT - RIGHT);
  const py = (y: number) =>
    TOP + ((yHi - y) / (yHi - yLo)) * (HEIGHT - TOP - BOTTOM);
  const num = (value: number) => fmt(value, { maximumFractionDigits: 2 });
  const xTicks = [Math.min(...xs), Math.max(...xs)];
  const yTicks = [Math.min(...ys), Math.max(...ys)];
  return (
    <figure>
      <svg
        aria-label={strings.plotLabel}
        className="w-full max-w-xl"
        role="img"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      >
        <title>{strings.plotLabel}</title>
        <line
          className="stroke-border"
          x1={LEFT}
          x2={LEFT}
          y1={TOP}
          y2={HEIGHT - BOTTOM}
        />
        <line
          className="stroke-border"
          x1={LEFT}
          x2={WIDTH - RIGHT}
          y1={HEIGHT - BOTTOM}
          y2={HEIGHT - BOTTOM}
        />
        <line
          className="stroke-primary"
          strokeWidth={2}
          x1={px(xLo)}
          x2={px(xHi)}
          y1={py(lineAt(xLo))}
          y2={py(lineAt(xHi))}
        />
        {fit.points.map((p) => (
          <circle
            className="fill-foreground"
            cx={px(p.x * X_SCALE)}
            cy={py(p.y)}
            key={`${p.t}-${p.k}`}
            r={4}
          />
        ))}
        {xTicks.map((value) => (
          <text
            className="fill-muted-foreground text-[11px]"
            key={`x${value}`}
            textAnchor="middle"
            x={px(value)}
            y={HEIGHT - BOTTOM + 16}
          >
            {num(value)}
          </text>
        ))}
        {yTicks.map((value) => (
          <text
            className="fill-muted-foreground text-[11px]"
            dominantBaseline="middle"
            key={`y${value}`}
            textAnchor="end"
            x={LEFT - 6}
            y={py(value)}
          >
            {num(value)}
          </text>
        ))}
        <text
          className="fill-muted-foreground text-[11px]"
          textAnchor="middle"
          x={(LEFT + WIDTH - RIGHT) / 2}
          y={HEIGHT - 8}
        >
          {strings.plotX}
        </text>
        <text
          className="fill-muted-foreground text-[11px]"
          textAnchor="middle"
          transform={`rotate(-90 14 ${(TOP + HEIGHT - BOTTOM) / 2})`}
          x={14}
          y={(TOP + HEIGHT - BOTTOM) / 2}
        >
          {strings.plotY}
        </text>
      </svg>
    </figure>
  );
}
